import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Undo as UndoIcon,
  Person as PersonIcon,
  Event as EventIcon,
  PendingActions as PendingActionsIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {
  getTasks,
  deleteTask,
  updateTask,
  TASK_STATUSES,
  PRIORITY_LABELS,
  CATEGORY_LABELS,
  PRIORITY_OPTIONS,
  CATEGORY_OPTIONS,
} from "../services/TaskService";
import {useMessageContext} from "../contexts/MessageContext";
import {RTL} from "../cache/CacheRtl";
import {Pagination} from "../componenets/ui";
import {borders, shadows} from "../componenets/ui/colors";
import {t} from "../componenets/dashboard/coach/coachTokens";
import TabsRow from "../componenets/dashboard/coach/TabsRow";
import TaskDialog from "../componenets/tasks/TaskDialog";
import TaskSuggestionsPanel from "../componenets/tasks/TaskSuggestionsPanel";
import dayjs from "dayjs";

/* ── Styled filter autocomplete (coach-token accent) ─────── */
const FilterSelect = styled(Autocomplete)({
  minWidth: "160px",
  "& .MuiOutlinedInput-root": {
    color: t.t2, borderRadius: t.rs,
    backgroundColor: t.white, fontSize: "14px", fontWeight: 500, height: "40px",
    "& .MuiOutlinedInput-notchedOutline": {borderColor: t.border},
    "&:hover .MuiOutlinedInput-notchedOutline": {borderColor: t.green},
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {borderColor: t.green, borderWidth: "2px"},
  },
  "& .MuiInputLabel-root": {color: t.t2, fontSize: "14px", "&.Mui-focused": {color: t.green}},
});

/* ── Constants & helpers ─────────────────────────────────── */
const PAGE_SIZE = 20;

const PRIORITY_STYLES = {
  HIGH: {backgroundColor: t.redLight, color: t.redText},
  MEDIUM: {backgroundColor: t.amberLight, color: t.amberText},
  LOW: {backgroundColor: t.greenLight, color: t.greenText},
};

const STATUS_TABS = [
  {key: "PENDING", label: "ממתינות", Icon: PendingActionsIcon},
  {key: "COMPLETED", label: "הושלמו", Icon: CheckCircleIcon},
];

/* ── Table cell presets (match Clients/CoachDashboard) ────── */
const td = {padding: "10px 12px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle"};
const th = {padding: "10px 12px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
            background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em"};

/* ═══════════════════════════════════════════════════════════
   Tasks page
   ═══════════════════════════════════════════════════════════ */
const TasksPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {notificationHandler} = useMessageContext();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [suggestionData, setSuggestionData] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuTask, setMenuTask] = useState(null);

  const [f, setF] = useState({
    tasks: [],
    total: 0,
    totalPages: 0,
    keyword: query.get("keyword") || "",
    priority: query.get("priority") || null,
    category: query.get("category") || null,
    status: query.get("status") || "PENDING",
    page: +(query.get("page") || 0),
    loading: true,
  });

  // URL sync
  const sync = useCallback((ns) => {
    const p = new URLSearchParams();
    if (ns.keyword) p.set("keyword", ns.keyword);
    if (ns.priority) p.set("priority", ns.priority);
    if (ns.category) p.set("category", ns.category);
    if (ns.status !== "PENDING") p.set("status", ns.status);
    if (ns.page > 0) p.set("page", ns.page);
    navigate(p.toString() ? `${location.pathname}?${p}` : location.pathname, {replace: true});
  }, [navigate, location.pathname]);

  const set = useCallback((patch) => { const ns = {...f, page: 0, ...patch}; setF(ns); sync(ns); }, [f, sync]);

  // Debounced search
  const [searchInput, setSearchInput] = useState(f.keyword);
  const debounceRef = useRef(null);
  const handleSearch = useCallback((val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => set({keyword: val}), 300);
  }, [set]);

  // Fetch tasks
  useEffect(() => {
    setF(p => ({...p, loading: true}));
    getTasks(f.page, PAGE_SIZE, {
      status: f.status,
      priority: f.priority,
      category: f.category,
      keyword: f.keyword,
    })
        .then(r => setF(p => ({
          ...p,
          tasks: r.data?.content || [],
          total: r.data?.totalElements || 0,
          totalPages: r.data?.totalPages || 0,
          loading: false,
        })))
        .catch(() => setF(p => ({...p, loading: false})));
  }, [f.page, f.keyword, f.priority, f.category, f.status]);

  const reload = () => {
    setF(p => ({...p, loading: true}));
    getTasks(f.page, PAGE_SIZE, {
      status: f.status,
      priority: f.priority,
      category: f.category,
      keyword: f.keyword,
    })
        .then(r => setF(p => ({
          ...p,
          tasks: r.data?.content || [],
          total: r.data?.totalElements || 0,
          totalPages: r.data?.totalPages || 0,
          loading: false,
        })))
        .catch(() => setF(p => ({...p, loading: false})));
  };

  const pages = Math.ceil(f.total / PAGE_SIZE);

  // Dialog handlers
  const handleClose = () => { setNewTaskOpen(false); setEditTask(null); setSuggestionData(null); };
  const handleEditTask = (task) => { setEditTask(task); setNewTaskOpen(true); };
  const handleSuggestionSelect = (suggestion) => { setSuggestionData(suggestion); setNewTaskOpen(true); };

  // Menu handlers
  const openMenu = (e, task) => { e.stopPropagation(); setAnchorEl(e.currentTarget); setMenuTask(task); };
  const closeMenu = () => { setAnchorEl(null); setMenuTask(null); };

  const handleDelete = async () => {
    if (!menuTask) return;
    try {
      await deleteTask(menuTask.id);
      notificationHandler.success("המשימה נמחקה בהצלחה");
      reload();
    } catch { notificationHandler.error("שגיאה במחיקת המשימה"); }
    closeMenu();
  };

  const handleToggleStatus = async () => {
    if (!menuTask) return;
    try {
      const newStatus = menuTask.status === TASK_STATUSES.PENDING ? TASK_STATUSES.COMPLETED : TASK_STATUSES.PENDING;
      await updateTask({id: menuTask.id, status: newStatus});
      notificationHandler.success(newStatus === TASK_STATUSES.COMPLETED ? "המשימה סומנה כהושלמה" : "המשימה הוחזרה לממתינות");
      reload();
    } catch { notificationHandler.error("שגיאה בעדכון המשימה"); }
    closeMenu();
  };

  // Due date display helper
  const renderDueDate = (task) => {
    if (!task.dueDate) return <Typography sx={{fontSize: "13px", color: t.t3}}>-</Typography>;
    const dueDate = dayjs(task.dueDate * 1000);
    const today = dayjs();
    const isOverdue = dueDate.isBefore(today, "day") && task.status === TASK_STATUSES.PENDING;
    const isToday = dueDate.isSame(today, "day");
    const isTomorrow = dueDate.isSame(today.add(1, "day"), "day");
    let dateText = dueDate.format("DD/MM/YYYY");
    if (isToday) dateText = "היום";
    if (isTomorrow) dateText = "מחר";
    return (
        <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
          <EventIcon sx={{fontSize: "16px", color: isOverdue ? t.redText : t.t3}}/>
          <Typography sx={{fontSize: "13px", color: isOverdue ? t.redText : (isToday ? t.amberText : t.t2), fontWeight: isOverdue || isToday ? 600 : 400}}>
            {dateText}
          </Typography>
        </Box>
    );
  };

  /* ── Render ──────────────────────────────────────────── */
  return (
      <Box sx={{display: "flex", flexDirection: "column", gap: "18px"}}>
        {/* Dialog */}
        <TaskDialog isOpen={newTaskOpen} close={handleClose} setReload={reload} task={editTask} suggestionData={suggestionData}/>

        {/* Actions menu */}
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
          <MenuItem onClick={() => { handleEditTask(menuTask); closeMenu(); }}>
            <EditIcon sx={{marginLeft: "8px", fontSize: "18px", color: t.t2}}/>
            <Typography sx={{fontSize: "14px"}}>עריכה</Typography>
          </MenuItem>
          <MenuItem onClick={handleToggleStatus}>
            {menuTask?.status === TASK_STATUSES.PENDING ? (
                <>
                  <CheckCircleIcon sx={{marginLeft: "8px", fontSize: "18px", color: t.green}}/>
                  <Typography sx={{fontSize: "14px"}}>סמן כהושלם</Typography>
                </>
            ) : (
                <>
                  <UndoIcon sx={{marginLeft: "8px", fontSize: "18px", color: t.t2}}/>
                  <Typography sx={{fontSize: "14px"}}>החזר לממתין</Typography>
                </>
            )}
          </MenuItem>
          <MenuItem onClick={handleDelete} sx={{color: t.red}}>
            <DeleteIcon sx={{marginLeft: "8px", fontSize: "18px"}}/>
            <Typography sx={{fontSize: "14px"}}>מחק</Typography>
          </MenuItem>
        </Menu>

        {/* ─── Status pill tabs (on canvas) ────────────── */}
        <TabsRow value={f.status} onChange={v => set({status: v})} tabs={STATUS_TABS}/>

        {/* ─── Title row (no white band — sits on canvas) ─── */}
        <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0}}>
          <Box>
            <Typography sx={{fontSize: {xs: "22px", md: "26px"}, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px"}}>
              משימות
            </Typography>
            <Typography sx={{fontSize: "13px", color: t.t2, mt: 0.25}}>ניהול משימות</Typography>
          </Box>
          <Box component="button" onClick={() => setNewTaskOpen(true)} sx={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            background: t.green, color: "#fff",
            height: 34, px: "15px", borderRadius: t.rs,
            fontSize: "13px", fontWeight: 600, letterSpacing: "-0.1px",
            border: "none", cursor: "pointer", fontFamily: "inherit",
            transition: "all .14s",
            "&:hover": {background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
          }}>
            <AddIcon sx={{fontSize: 16}}/>
            משימה חדשה
          </Box>
        </Box>

        {/* ─── Suggestions (pending tab only) ──────────── */}
        {f.status === "PENDING" && (
            <TaskSuggestionsPanel onSelect={handleSuggestionSelect}/>
        )}

        {/* ─── Table card ──────────────────────────────── */}
        <Box sx={{
          background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r,
          boxShadow: t.sh, overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}>

          {/* Filter bar inside card */}
          <Box sx={{
            display: "flex", alignItems: "center", gap: "7px",
            padding: "10px 14px", borderBottom: `1px solid ${t.border}`,
            background: t.bg, flexWrap: "wrap", flexShrink: 0,
          }}>
            {/* Count pill */}
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: "4px",
              fontSize: "12px", fontWeight: 700, color: t.greenText,
              background: t.greenLight, border: "1px solid rgba(1,187,167,.2)",
              padding: "4px 12px", borderRadius: "20px", whiteSpace: "nowrap",
            }}>
              <AssignmentIcon sx={{fontSize: 14}}/>
              {f.total} משימות
            </Box>

            {/* Search */}
            <Box sx={{position: "relative"}}>
              <SearchIcon sx={{position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: t.t3, pointerEvents: "none"}}/>
              <InputBase
                  value={searchInput}
                  onChange={e => handleSearch(e.target.value)}
                  placeholder="חיפוש משימה..."
                  sx={{
                    width: 200,
                    padding: "6px 10px 6px 30px",
                    border: `1.5px solid ${t.border}`,
                    borderRadius: t.rs,
                    background: t.white,
                    color: t.text,
                    fontSize: "12px",
                    "& input": {padding: 0},
                    "& input::placeholder": {color: t.t3, opacity: 1},
                    "&:focus-within": {borderColor: t.green, boxShadow: "0 0 0 3px rgba(1,187,167,.08)"},
                  }}
              />
            </Box>

            {/* Priority filter */}
            <RTL>
              <FilterSelect size="small"
                            options={PRIORITY_OPTIONS}
                            getOptionLabel={o => o.label || ""}
                            value={PRIORITY_OPTIONS.find(o => o.value === f.priority) || null}
                            onChange={(_, v) => set({priority: v?.value || null})}
                            isOptionEqualToValue={(o, v) => o.value === v.value}
                            renderInput={p => <TextField {...p} label="עדיפות" size="small"/>}
                            slotProps={{popper: {sx: {zIndex: 1300}}, paper: {sx: {borderRadius: borders.radius.md, border: borders.default, boxShadow: shadows.dropdown}}}}
              />
            </RTL>

            {/* Category filter */}
            <RTL>
              <FilterSelect size="small"
                            options={CATEGORY_OPTIONS}
                            getOptionLabel={o => o.label || ""}
                            value={CATEGORY_OPTIONS.find(o => o.value === f.category) || null}
                            onChange={(_, v) => set({category: v?.value || null})}
                            isOptionEqualToValue={(o, v) => o.value === v.value}
                            renderInput={p => <TextField {...p} label="קטגוריה" size="small"/>}
                            slotProps={{popper: {sx: {zIndex: 1300}}, paper: {sx: {borderRadius: borders.radius.md, border: borders.default, boxShadow: shadows.dropdown}}}}
              />
            </RTL>

            <Box sx={{flex: 1}}/>
          </Box>

          {/* Table area */}
          {f.loading && !f.tasks.length ? (
              <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 540}}>
                <CircularProgress size={32} sx={{color: t.green}}/>
              </Box>
          ) : !f.tasks.length ? (
              <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240, flexDirection: "column", gap: 1, padding: "48px"}}>
                <AssignmentIcon sx={{fontSize: "48px", color: t.border}}/>
                <Typography sx={{color: t.t3, fontSize: "15px", fontWeight: 500}}>אין משימות</Typography>
                <Typography sx={{color: t.t3, fontSize: "13px"}}>נסה לשנות את הסינון או צור משימה חדשה</Typography>
              </Box>
          ) : (
              <>
                <TableContainer sx={{overflowY: "auto", maxHeight: 540}}>
                  <Table stickyHeader sx={{tableLayout: "fixed"}}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{...th, textAlign: "right"}}>משימה</TableCell>
                        <TableCell sx={{...th, textAlign: "right", width: "15%", display: {xs: "none", md: "table-cell"}}}>לקוח</TableCell>
                        <TableCell sx={{...th, textAlign: "right", width: "12%", display: {xs: "none", md: "table-cell"}}}>קטגוריה</TableCell>
                        <TableCell sx={{...th, textAlign: "right", width: "10%", display: {xs: "none", md: "table-cell"}}}>עדיפות</TableCell>
                        <TableCell sx={{...th, textAlign: "right", width: "13%", display: {xs: "none", md: "table-cell"}}}>תאריך יעד</TableCell>
                        <TableCell sx={{...th, textAlign: "left", width: 60}}>פעולות</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {f.tasks.map(task => (
                          <TableRow key={task.id} onClick={() => handleEditTask(task)}
                                    sx={{cursor: "pointer", "&:hover td": {background: t.greenLight}, transition: "background .12s ease"}}>
                            {/* Title */}
                            <TableCell sx={{...td, textAlign: "right"}}>
                              <Typography sx={{
                                fontSize: "13px", fontWeight: 600, color: t.text,
                                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                textDecoration: task.status === TASK_STATUSES.COMPLETED ? "line-through" : "none",
                                mb: task.description ? 0.5 : 0,
                              }}>
                                {task.title}
                              </Typography>
                              {task.description && (
                                  <Tooltip title={task.description} placement="top">
                                    <Typography sx={{fontSize: "12px", color: t.t2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "280px"}}>
                                      {task.description}
                                    </Typography>
                                  </Tooltip>
                              )}
                            </TableCell>

                            {/* Client */}
                            <TableCell sx={{...td, textAlign: "right", display: {xs: "none", md: "table-cell"}}}>
                              {task.traineeName ? (
                                  <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                                    <PersonIcon sx={{fontSize: "16px", color: t.t2}}/>
                                    <Typography sx={{fontSize: "13px", fontWeight: 500, color: t.t2}}>{task.traineeName}</Typography>
                                  </Box>
                              ) : (
                                  <Typography sx={{fontSize: "13px", color: t.t3}}>-</Typography>
                              )}
                            </TableCell>

                            {/* Category */}
                            <TableCell sx={{...td, textAlign: "right", display: {xs: "none", md: "table-cell"}}}>
                              <Typography sx={{fontSize: "13px", color: task.category ? t.t2 : t.t3}}>
                                {task.category ? (CATEGORY_LABELS[task.category] || task.category) : "-"}
                              </Typography>
                            </TableCell>

                            {/* Priority */}
                            <TableCell sx={{...td, textAlign: "right", display: {xs: "none", md: "table-cell"}}}>
                              <RTL>
                                <Chip label={PRIORITY_LABELS[task.priority] || task.priority} size="small" sx={{
                                  ...(PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.MEDIUM),
                                  fontSize: "11px", height: "24px", fontWeight: 600,
                                }}/>
                              </RTL>
                            </TableCell>

                            {/* Due Date */}
                            <TableCell sx={{...td, textAlign: "right", display: {xs: "none", md: "table-cell"}}}>
                              {renderDueDate(task)}
                            </TableCell>

                            {/* Actions */}
                            <TableCell sx={{...td, textAlign: "left", width: 60}}>
                              <IconButton onClick={(e) => openMenu(e, task)} size="small" sx={{color: t.t2}}>
                                <MoreVertIcon fontSize="small"/>
                              </IconButton>
                            </TableCell>
                          </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {pages > 1 && (
                    <Pagination currentPage={f.page} totalPages={pages} totalItems={f.total} itemsPerPage={PAGE_SIZE}
                                onPageChange={p => { const ns = {...f, page: p}; setF(ns); sync(ns); }}/>
                )}
              </>
          )}
        </Box>
      </Box>
  );
};

export default TasksPage;
